// UI-3, decision 7: Undo on deletes, built once and used by every delete on Business pages.
//
//   remove(item)  the item disappears at once and a toast offers Undo for 6 seconds; only then is
//                 the real DELETE sent
//   Undo          puts it back; nothing was ever sent
//   page hidden, closed or navigated away from inside the window
//                 the DELETE is sent at once, with keepalive so it survives the page going away
//                 (pagehide, visibilitychange to hidden, and the page component unmounting, which
//                 is what navigating inside the app does)
//   the DELETE fails
//                 the item comes back, and the toast says the server's own words
//
// The caller owns the list and filters out isHidden(item.id); onDeleted(item) is where it drops
// the item for good. send(item, { keepalive }) does the request:
//   send: (c, { keepalive }) => api(`/api/contacts/${c.id}`, { method: 'DELETE', keepalive })
//
// onRestore(item, { undone, error }) is optional, for a caller whose list it cannot simply filter
// (Aidey Chat's typed cancel, decision 75: App's task list is refetched underneath it). It runs
// when the item comes back: on Undo (undone: true) or when the DELETE fails (error).
import { useCallback, useEffect, useRef, useState } from 'react';
import { showToast, showErrorToast } from '../utils/toast';
import { messageFromError } from '../utils/apiError';

export const UNDO_MS = 6000;

export function useDelayedDelete({ send, onDeleted, onRestore, describe = () => 'Deleted.', undoLabel = 'Undo', failMessage = 'That could not be deleted.', ms = UNDO_MS }) {
  const [hidden, setHidden] = useState(() => new Set());
  const pending = useRef(new Map());   // id -> { item, timer, toast }
  const mounted = useRef(true);
  const latest = useRef({ send, onDeleted });
  latest.current = { send, onDeleted, onRestore };

  const hide = (id) => setHidden((h) => new Set(h).add(id));
  const show = (id) => { if (mounted.current) setHidden((h) => { const n = new Set(h); n.delete(id); return n; }); };

  // Send the DELETE for one pending item now. keepalive when the page may be going away.
  const commit = useCallback(async (id, { keepalive = false } = {}) => {
    const p = pending.current.get(id);
    if (!p) return;
    pending.current.delete(id);
    clearTimeout(p.timer);
    p.toast?.remove?.();
    try {
      await latest.current.send(p.item, { keepalive });
      latest.current.onDeleted?.(p.item);
      show(id);   // the caller has dropped it from its list; no need to keep hiding it
    } catch (err) {
      show(id);
      latest.current.onRestore?.(p.item, { undone: false, error: err });
      if (mounted.current && !keepalive) showErrorToast(messageFromError(err, failMessage));
    }
  }, [failMessage]);

  const undo = useCallback((id) => {
    const p = pending.current.get(id);
    if (!p) return;
    pending.current.delete(id);
    clearTimeout(p.timer);
    show(id);
    latest.current.onRestore?.(p.item, { undone: true });
  }, []);

  const remove = useCallback((item) => {
    const { id } = item;
    if (pending.current.has(id)) return;
    hide(id);
    const toast = showToast(describe(item), { type: 'info', ms, action: { label: undoLabel, onClick: () => undo(id) } });
    const timer = setTimeout(() => { commit(id); }, ms);
    pending.current.set(id, { item, timer, toast });
  }, [describe, ms, undoLabel, undo, commit]);

  // Everything still waiting goes now (keepalive): the page is going away.
  const flush = useCallback(() => {
    for (const id of [...pending.current.keys()]) commit(id, { keepalive: true });
  }, [commit]);

  // Attached once: a re-render must never count as "the page went away" and send early.
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => {
    mounted.current = true;
    const onHide = () => flushRef.current();
    const onVisibility = () => { if (document.visibilityState === 'hidden') flushRef.current(); };
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onVisibility);
      mounted.current = false;
      flushRef.current();   // navigating away inside the app unmounts the page
    };
  }, []);

  const isHidden = useCallback((id) => hidden.has(id), [hidden]);
  return { remove, undo, flush, isHidden, pendingCount: hidden.size };
}
